// Agent Console — admin, strictly READ-ONLY data-analyst agent (Claude Agent SDK).
//
// Mirrors two backend contracts:
//   * REST shapes  → app/schemas/agent_console.py
//   * SSE wire union → services/agent_console/runtime/events.py
//
// REST calls go through the shared axios `api` instance (baseURL '/api', Bearer
// auto-attached). The streaming turn uses raw `fetch` (axios doesn't stream in
// the browser) and MUST attach the Bearer token itself: the backend
// authenticates the Authorization header (or X-API-Key) only, never a cookie.

import api, { tokenService, refreshAccessToken } from './api';
// Pure lift logic (type-only imports back into this module — no runtime cycle).
import {
  liftAgentFileCards,
  type AgentFileData,
} from '../components/agentConsole/agentFileCards';
import {
  liftConversationCards,
  type ConversationDecisionData,
  type SentMessageData,
} from '../components/agentConsole/conversationMessageCards';

// ---------------------------------------------------------------------------
// Enums (mirror db/models/agent_console.py — serialized as lowercase strings)
// ---------------------------------------------------------------------------

export type ConversationState =
  | 'created'
  | 'running'
  | 'awaiting_input'
  | 'failed'
  | 'cancelled'
  | 'archived';

export type MessageRole = 'user' | 'assistant' | 'system';

export type ToolCallOutcome = 'denied' | 'ok' | 'error';

// A turn may only START from a non-running, non-archived state (FSM in
// db/models/agent_console.py). The input box is disabled otherwise.
export function canStartTurn(state: ConversationState): boolean {
  return state !== 'running' && state !== 'archived';
}

/**
 * Honest composer placeholder for the state the thread is actually in. The
 * composer is only truly *closed* when the thread is archived or you're viewing
 * someone else's — a `running` thread means Merlin is mid-turn (not closed), and
 * a turn streaming in a *different* conversation must not read as "closed" on the
 * one you're looking at. Pure so every branch is unit-tested.
 *
 * - `streamingHere`: a turn is streaming in THIS conversation (steer/Stop shown).
 * - `streamingElsewhere`: a turn is streaming in a different conversation.
 */
export function composerPlaceholder(opts: {
  streamingHere: boolean;
  streamingElsewhere: boolean;
  canSend: boolean;
  viewerIsOwner: boolean;
  state: ConversationState | null;
  /** No agent answers to this thread's id any more — a retired-agent thread. */
  profileRetired?: boolean;
}): string {
  if (opts.streamingHere) return 'Steer the agent with more context, or press Stop…';
  if (opts.canSend) return 'Ask about orders, customers, products…';
  // Ahead of the generic closed/read-only lines: those say the thread is over,
  // this says WHY and that the transcript above is still good.
  if (opts.profileRetired) return 'This agent was retired — the transcript stays readable.';
  if (opts.streamingElsewhere) return 'Merlin is working in another conversation…';
  if (!opts.viewerIsOwner) return 'Read-only — this is someone else’s conversation.';
  if (opts.state === 'running') return 'Merlin is working…';
  if (opts.state === 'archived') return 'This analysis is archived.';
  return 'This analysis is closed.';
}

// ---------------------------------------------------------------------------
// REST read/write models (mirror app/schemas/agent_console.py)
// ---------------------------------------------------------------------------

export interface ConversationRead {
  id: string;
  title: string | null;
  state: ConversationState;
  model: string;
  // The console profile this conversation last ran under. Restored into the
  // profile selector when the thread is reloaded. null for legacy rows that
  // predate profile persistence (treated as the default profile).
  profile_id: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  last_activity_at: string;
  archived_at: string | null;
  // Attribution — who started this conversation.
  user_id: string;
  owner_name: string | null;
  owner_email: string | null;
  // Replayed-history cap (turns the model still sees each turn); set by doors
  // whose profile caps history (e.g. Tech Support Co-Pilot). null/absent =
  // unbounded replay (the default for most profiles).
  history_max_turns?: number | null;
  // Server-resolved display name of the profile this conversation runs under,
  // from the FULL profile registry (picker + door + headless) — so a door
  // thread names its real agent instead of falling back to "Analyst". null for
  // legacy rows that predate profile persistence.
  profile_name?: string | null;
  // True when the conversation runs a NON-PICKER (door-locked/headless)
  // profile. The console renders a fixed profile label instead of the selector,
  // and the server ignores any client-sent profile_id on this thread.
  profile_locked?: boolean;
  // True when NO agent answers to this thread's stored id any more — the handful
  // of threads whose agent was retired. The server REFUSES a turn on these (it
  // would otherwise substitute the default Analyst under a label naming
  // something else), so the composer is disabled rather than offering a Send
  // that can only fail.
  profile_retired?: boolean;
}

export interface ConversationList {
  items: ConversationRead[];
}

export interface ConversationOwner {
  user_id: string;
  name: string | null;
  email: string | null;
  conversation_count: number;
}

export interface MessageRead {
  id: string;
  seq: number;
  role: MessageRole;
  content: string;
  raw_blocks: Array<Record<string, unknown>> | null;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_creation_input_tokens: number | null;
  // 1h-TTL subset of cache_creation_input_tokens; null = split not reported.
  cache_creation_1h_input_tokens?: number | null;
  cache_read_input_tokens: number | null;
  cost_usd: number | null;
  stop_reason: string | null;
  created_at: string;
}

export interface ToolCallRead {
  id: string;
  tool_use_id: string | null;
  tool_name: string;
  // The human-readable Knowledge Bank this call targeted, derived server-side
  // from tool_name (mirror ToolCallRead.kb). null for non-KB tools.
  kb?: string | null;
  tool_input: Record<string, unknown>;
  outcome: ToolCallOutcome;
  permission_reason: string | null;
  result_content: Array<Record<string, unknown>> | null;
  duration_ms: number | null;
  created_at: string;
}

export interface ConversationTotals {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  // How many of the cache-write tokens used the 1-hour TTL (rest were 5m).
  cache_creation_1h_input_tokens: number;
  cache_read_input_tokens: number;
  cost_usd: number;
  turn_count: number;
}

/**
 * One uploaded conversation attachment (bytes live in GCS server-side).
 * `message_id` is stamped post-turn — the user message the file rode in on;
 * null = uploaded but not yet sent with a message.
 */
export interface ConversationAttachmentRead {
  id: string;
  conversation_id: string;
  message_id: string | null;
  filename: string;
  mime_type: string;
  size_bytes: number;
  gs_uri: string;
  created_at: string | null;
}

export interface ConversationDetail {
  conversation: ConversationRead;
  messages: MessageRead[];
  tool_calls: ToolCallRead[];
  totals: ConversationTotals;
  // The conversation's uploaded files, so a reloaded transcript renders the
  // attachment chips on their user messages. Optional: older payloads omit it.
  attachments?: ConversationAttachmentRead[];
  // False when viewing someone else's conversation via a shared link — the UI
  // shows it read-only (no composer).
  viewer_is_owner: boolean;
}

/**
 * Upload one composer attachment (image/pdf/csv/xlsx/txt, ≤25 MiB) to a
 * conversation BEFORE posting the message that sends it. Shared by every
 * Agent-SDK chat door — pass the door's base path (e.g. '/analyst-copilot',
 * '/quality-copilot', '/tech-support-copilot', '/admin/agent-console').
 */
export async function uploadConversationAttachment(
  base: string,
  conversationId: string,
  file: File,
): Promise<ConversationAttachmentRead> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<ConversationAttachmentRead>(
    `${base}/conversations/${conversationId}/attachments`,
    form,
    // The shared axios instance defaults Content-Type to application/json,
    // which clobbers FormData; the explicit multipart header makes axios emit
    // the real multipart body + boundary (repo convention — see brainDump.ts).
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * Fetch one stored conversation attachment's bytes (auth rides the shared
 * axios instance). Serves agent-generated files (attach_file) and user
 * uploads alike; the caller turns the blob into an object URL for preview or
 * a download click. Shared by every Agent-SDK chat door — pass the door's
 * base path, same as `uploadConversationAttachment`.
 */
export async function downloadConversationAttachment(
  base: string,
  conversationId: string,
  attachmentId: string,
): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `${base}/conversations/${conversationId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' },
  );
  return data;
}

/**
 * Format a USD cost for the UI. Shows fractional cents because a single agent
 * turn often comes in under $0.05; falls back to whole cents when the value is
 * already a "clean" multiple to avoid trailing zeros.
 */
export function formatCostUsd(cost: number | null | undefined): string {
  if (cost == null) return '';
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(4)}`;
}

/**
 * Honest token label. The meter historically showed input+output only, but the
 * Anthropic/SDK usage object reports input_tokens as the UNCACHED remainder —
 * the cached system prefix (cache_creation + cache_read) is billed separately
 * and for a fresh agent turn usually dwarfs the visible count. We surface it as
 * "(+N cached)" so the token number squares with the cost. Returns null when no
 * token data is present (e.g. a user message).
 */
export function formatTokens(usage: {
  input_tokens: number | null;
  output_tokens: number | null;
  cache_creation_input_tokens?: number | null;
  cache_read_input_tokens?: number | null;
}): string | null {
  if (usage.input_tokens == null && usage.output_tokens == null) return null;
  const shown = (usage.input_tokens ?? 0) + (usage.output_tokens ?? 0);
  const cached =
    (usage.cache_creation_input_tokens ?? 0) +
    (usage.cache_read_input_tokens ?? 0);
  const base = `${shown.toLocaleString()} tokens`;
  return cached > 0 ? `${base} (+${cached.toLocaleString()} cached)` : base;
}

/**
 * Tooltip copy for the running-total badge: names the cache-write and
 * cache-read tokens billed in the cost and, when any write used the 1-hour
 * TTL (the human doors' resumed follow-up turns), how many — so the meter
 * shows which cache TTL actually hit rather than leaving it to be inferred
 * from the dollar figure. Returns the plain "Running total" copy when the
 * conversation has no cache tokens at all.
 */
export function describeTotalsTooltip(totals: ConversationTotals): string {
  const cached =
    totals.cache_creation_input_tokens + totals.cache_read_input_tokens;
  if (cached <= 0) return 'Running total for this conversation';
  const oneHour = totals.cache_creation_1h_input_tokens ?? 0;
  const write =
    oneHour > 0
      ? `${totals.cache_creation_input_tokens.toLocaleString()} cache-write (${oneHour.toLocaleString()} at the 1h TTL)`
      : `${totals.cache_creation_input_tokens.toLocaleString()} cache-write`;
  return `Running total — includes ${write} + ${totals.cache_read_input_tokens.toLocaleString()} cache-read tokens billed in the cost`;
}

/**
 * Pure helper that mirrors the backend totals over the in-memory transcript.
 * Used to fold the live in-flight turn into the displayed totals so the meter
 * keeps moving while the SDK is still streaming.
 */
export function addUsageToTotals(
  totals: ConversationTotals,
  delta: {
    input_tokens: number | null;
    output_tokens: number | null;
    cache_creation_input_tokens?: number | null;
    cache_creation_1h_input_tokens?: number | null;
    cache_read_input_tokens?: number | null;
    cost_usd: number | null;
  },
): ConversationTotals {
  return {
    input_tokens: totals.input_tokens + (delta.input_tokens ?? 0),
    output_tokens: totals.output_tokens + (delta.output_tokens ?? 0),
    cache_creation_input_tokens:
      totals.cache_creation_input_tokens +
      (delta.cache_creation_input_tokens ?? 0),
    cache_creation_1h_input_tokens:
      (totals.cache_creation_1h_input_tokens ?? 0) +
      (delta.cache_creation_1h_input_tokens ?? 0),
    cache_read_input_tokens:
      totals.cache_read_input_tokens + (delta.cache_read_input_tokens ?? 0),
    cost_usd: totals.cost_usd + (delta.cost_usd ?? 0),
    turn_count: totals.turn_count + 1,
  };
}

export const EMPTY_TOTALS: ConversationTotals = {
  input_tokens: 0,
  output_tokens: 0,
  cache_creation_input_tokens: 0,
  cache_creation_1h_input_tokens: 0,
  cache_read_input_tokens: 0,
  cost_usd: 0,
  turn_count: 0,
};

export interface ConversationCreate {
  title?: string | null;
  model?: string | null;
  // Console profile to run the new thread under (default: the Analyst). Persisted
  // on the conversation so it restores on reload; unknown/empty -> the default.
  profile_id?: string | null;
}

// ---------------------------------------------------------------------------
// SSE event union (mirror services/agent_console/runtime/events.py)
//
// NOTE: the end-of-turn accounting frame's discriminator is "result" (UsageEvent),
// and text/thinking frames carry `text` (not `content`).
// ---------------------------------------------------------------------------

export interface StartedEvent {
  type: 'started';
  conversation_id: string;
  state: string;
  // Identifies this turn so a Stop click can target it precisely (and a stale
  // cancel for a previous turn is ignored). Null for turns run without a token.
  run_id: string | null;
}

export interface TextEvent {
  type: 'text';
  text: string;
}

export interface ThinkingEvent {
  type: 'thinking';
  text: string;
}

export interface ToolUseEvent {
  type: 'tool_use';
  tool_use_id: string | null;
  name: string;
  // The human-readable Knowledge Bank this call targets when `name` is a native
  // KB mount (query_knowledge_bank / report_learning); null/absent otherwise.
  kb?: string | null;
  input: Record<string, unknown>;
  allowed: boolean;
  reason: string;
}

export interface ToolResultEvent {
  type: 'tool_result';
  tool_use_id: string | null;
  is_error: boolean;
  summary: string;
}

export interface UsageEvent {
  type: 'result';
  stop_reason: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_creation_input_tokens: number | null;
  cache_creation_1h_input_tokens?: number | null;
  cache_read_input_tokens: number | null;
  cost_usd: number | null;
}

export interface StateEvent {
  type: 'state';
  state: string;
  error_message: string | null;
}

export interface TitleEvent {
  type: 'title';
  title: string;
}

export interface InjectedUserEvent {
  type: 'injected';
  text: string;
}

export interface CancelledEvent {
  type: 'cancelled';
  message: string;
}

export interface NoticeEvent {
  // A non-fatal note about HOW this turn is being answered. `context_rollover`:
  // the conversation's earlier turns exceeded the model's context window, so
  // the answer that follows starts from a fresh context (the thread itself
  // continues; nothing was lost from the audit). Streamed BEFORE the answer.
  type: 'notice';
  // `compacting`: the message started a new topic on a long thread, so the
  // session is compacted before the answer (kanban ea90fcc0).
  kind: 'context_rollover' | 'compacting';
  message: string;
}

export interface ErrorEvent {
  type: 'error';
  message: string;
}

export interface DoneEvent {
  type: 'done';
  // On a SUCCESSFUL turn: the persisted assistant message id + the model that
  // actually answered, so an ephemeral client turn can attach 👍/👎 feedback to
  // the REAL audit row and the "try a stronger model" nudge knows what ran. Both
  // are null/absent on a failed/cancelled/empty terminal frame.
  message_id?: string | null;
  model?: string | null;
}

export interface FastpathEvent {
  // Playbook fast path (Merlin two-tier routing): this turn was matched to an
  // existing Playbook entry and routed to the instant model. Emitted before
  // `started`; absent on slow-path turns.
  type: 'fastpath';
  model: string;
  entry_ids: string[];
  top_score?: number | null;
}

export type ConsoleEvent =
  | StartedEvent
  | FastpathEvent
  | TextEvent
  | ThinkingEvent
  | ToolUseEvent
  | ToolResultEvent
  | UsageEvent
  | StateEvent
  | TitleEvent
  | InjectedUserEvent
  | CancelledEvent
  | NoticeEvent
  | ErrorEvent
  | DoneEvent;

const CONSOLE_EVENT_TYPES: ReadonlySet<ConsoleEvent['type']> = new Set([
  'started',
  'fastpath',
  'text',
  'thinking',
  'tool_use',
  'tool_result',
  'result',
  'state',
  'title',
  'injected',
  'cancelled',
  'notice',
  'error',
  'done',
]);

// ---------------------------------------------------------------------------
// Pure SSE parsing helpers (no network — unit-tested directly)
// ---------------------------------------------------------------------------

/**
 * Split a raw SSE buffer on the blank-line record separator. Returns every
 * complete record plus the leftover (possibly incomplete) tail to carry into
 * the next chunk.
 */
export function splitSseRecords(buffer: string): { records: string[]; rest: string } {
  const records: string[] = [];
  let rest = buffer;
  let idx = rest.indexOf('\n\n');
  while (idx !== -1) {
    records.push(rest.slice(0, idx));
    rest = rest.slice(idx + 2);
    idx = rest.indexOf('\n\n');
  }
  return { records, rest };
}

/**
 * Parse one SSE record into a typed console event. Returns null when the record
 * carries no usable `data:` line, holds malformed JSON, or has an unknown
 * discriminator — the server is the source of truth, so we drop noise quietly.
 */
export function parseConsoleEvent(record: string): ConsoleEvent | null {
  for (const line of record.split('\n')) {
    if (!line.startsWith('data:')) continue;
    const body = line.slice(5).trim(); // .trim() also drops a trailing \r
    if (!body) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(body);
    } catch {
      return null;
    }
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { type?: unknown }).type === 'string' &&
      CONSOLE_EVENT_TYPES.has((parsed as { type: ConsoleEvent['type'] }).type)
    ) {
      return parsed as ConsoleEvent;
    }
    return null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tool-call view model (shared by the persisted trace + the live stream)
// ---------------------------------------------------------------------------

export type ToolCallStatus = 'ok' | 'denied' | 'error' | 'pending';

export interface ToolCallView {
  key: string;
  toolUseId: string | null;
  name: string; // prettified
  rawName: string; // fully-qualified
  // The Knowledge Bank this call targeted (a query_knowledge_bank /
  // report_learning into a mounted bank), or null for any non-KB tool. Lets the
  // trace name WHICH bank a multi-KB profile queried or wrote.
  kb: string | null;
  input: Record<string, unknown>;
  status: ToolCallStatus;
  reason: string | null;
  resultSummary: string;
  isError: boolean;
  durationMs: number | null;
  createdAt: string | null;
}

const MCP_PREFIX = 'mcp__';
const CONSOLE_TOOL_PREFIX = 'mcp__agent_console__';

/**
 * Prettify a qualified tool name for display:
 * `mcp__agent_console__pg_query` → `pg_query`; a generic `mcp__server__tool`
 * collapses to `tool`; anything else is returned unchanged.
 */
export function prettyToolName(toolName: string): string {
  if (toolName.startsWith(CONSOLE_TOOL_PREFIX)) {
    return toolName.slice(CONSOLE_TOOL_PREFIX.length);
  }
  if (toolName.startsWith(MCP_PREFIX)) {
    const parts = toolName.split('__');
    if (parts.length >= 3) return parts.slice(2).join('__');
  }
  return toolName;
}

/**
 * Flatten persisted `result_content` blocks (audit JSON) into readable text,
 * mirroring the backend's join-on-newline summary.
 */
export function summarizeResultContent(
  content: Array<Record<string, unknown>> | null,
): string {
  if (!content) return '';
  return content
    .map((block) => (typeof block.text === 'string' ? block.text : ''))
    .filter((text) => text.length > 0)
    .join('\n');
}

export function toolStatusVariant(
  status: ToolCallStatus,
): 'success' | 'warning' | 'error' | 'neutral' {
  switch (status) {
    case 'ok':
      return 'success';
    case 'denied':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'neutral';
  }
}

export function toolStatusLabel(status: ToolCallStatus): string {
  switch (status) {
    case 'ok':
      return 'ok';
    case 'denied':
      return 'denied';
    case 'error':
      return 'error';
    default:
      return 'running';
  }
}

/** Build a display view from a persisted tool-call audit row. */
export function toolCallViewFromRead(tc: ToolCallRead): ToolCallView {
  return {
    key: tc.id,
    toolUseId: tc.tool_use_id,
    name: prettyToolName(tc.tool_name),
    rawName: tc.tool_name,
    kb: tc.kb ?? null,
    input: tc.tool_input,
    status: tc.outcome,
    reason: tc.permission_reason,
    resultSummary: summarizeResultContent(tc.result_content),
    isError: tc.outcome === 'error',
    durationMs: tc.duration_ms,
    createdAt: tc.created_at,
  };
}

/** Build a live view from a streamed tool_use frame (result fills in later). */
export function toolCallViewFromUse(evt: ToolUseEvent, fallbackKey: string): ToolCallView {
  return {
    key: evt.tool_use_id ?? fallbackKey,
    toolUseId: evt.tool_use_id,
    name: prettyToolName(evt.name),
    rawName: evt.name,
    kb: evt.kb ?? null,
    input: evt.input ?? {},
    status: evt.allowed ? 'pending' : 'denied',
    reason: evt.reason ?? null,
    resultSummary: '',
    isError: false,
    durationMs: null,
    createdAt: null,
  };
}

// ---------------------------------------------------------------------------
// Block-by-block render model
//
// The orchestrator persists each assistant turn's full ``raw_blocks`` — the
// model's actual interleaving of text / extended-thinking / tool_use chunks.
// Rendering off that interleaving (not off the concatenated ``content`` plus
// a separate tool-call list) is what stops the UI clustering every tool call
// above one wall of text. Tool calls live in their own audit table; we re-pair
// them with the in-order tool_use block by ``tool_use_id``.
// ---------------------------------------------------------------------------

export type AssistantBlock =
  | { kind: 'text'; text: string }
  | { kind: 'thinking'; text: string }
  | { kind: 'tool'; tool: ToolCallView };

/**
 * Walk an assistant message's persisted ``raw_blocks`` into a render-ready
 * sequence of text / thinking / tool blocks, pairing each tool_use entry with
 * its full audit row by ``tool_use_id``. Consecutive text-only or thinking-only
 * SSE chunks coalesce into one block so paragraph breaks live at intent
 * boundaries, not between every streamed token.
 *
 * Legacy rows without ``raw_blocks`` (older than the block-render change) fall
 * back to a single text block from ``content`` so old transcripts still render.
 */
export function expandAssistantBlocks(
  message: MessageRead,
  toolCallsById: Map<string, ToolCallView>,
): AssistantBlock[] {
  if (!message.raw_blocks) {
    return message.content ? [{ kind: 'text', text: message.content }] : [];
  }
  const out: AssistantBlock[] = [];
  for (const block of message.raw_blocks) {
    const type = typeof block.type === 'string' ? block.type : '';
    if (type === 'text' && typeof block.text === 'string') {
      pushText(out, block.text);
    } else if (type === 'thinking' && typeof block.text === 'string') {
      pushThinking(out, block.text);
    } else if (type === 'tool_use') {
      const id = typeof block.id === 'string' ? block.id : null;
      const tool = id ? toolCallsById.get(id) : undefined;
      if (tool) {
        out.push({ kind: 'tool', tool });
      }
      // A tool_use with no matching audit row is dropped — the tool table is
      // the source of truth for what actually ran; a dangling reference would
      // render as a phantom button with no result.
    }
  }
  return out;
}

/**
 * Split an assistant turn into the intermediate work (thinking + tool calls +
 * any earlier text narrations) and the final answer. The final answer is the
 * model's CLOSING run of user-facing ``text`` blocks; everything before it
 * stays hidden behind the "view trace" toggle.
 *
 * We pivot on the last text run, NOT the last tool call. A model routinely
 * emits a ``thinking`` block right before its closing text ("let me compose the
 * answer…"), and on a no-tool turn the whole turn is ``[thinking, text]`` — both
 * cases left a thinking block inside ``finalAnswer`` when the pivot was the last
 * tool, so the italic thought rendered inline above the answer. Pivoting on the
 * trailing text run keeps ``finalAnswer`` purely the answer text and pushes the
 * thinking into the (collapsed) trace.
 *
 * A still-streaming turn whose final text hasn't arrived yet has
 * ``finalAnswer === []`` so the renderer falls back to the live status panel.
 */
export function splitFinalAnswer(blocks: AssistantBlock[]): {
  intermediate: AssistantBlock[];
  finalAnswer: AssistantBlock[];
} {
  // Walk back over the trailing run of `text` blocks; the first non-text block
  // (thinking or tool) ends the answer. firstFinalText stays at blocks.length
  // when the turn doesn't end in text (still streaming / ended on a tool).
  let firstFinalText = blocks.length;
  for (let i = blocks.length - 1; i >= 0; i--) {
    if (blocks[i].kind === 'text') {
      firstFinalText = i;
    } else {
      break;
    }
  }
  return {
    intermediate: blocks.slice(0, firstFinalText),
    finalAnswer: blocks.slice(firstFinalText),
  };
}

export type CurrentPhase = {
  // The most-recent narration the model emitted ("I'll load the schema first")
  // or a thinking pane. Null when the turn starts with a tool call before any
  // text.
  narration: AssistantBlock | null;
  // Tool calls the model issued under that narration, in arrival order.
  tools: ToolCallView[];
};

/**
 * From the intermediate work, extract just the latest "phase" — the most
 * recent narration plus the tool calls that ran under it. This is what the
 * live bubble displays while the agent is still iterating; each new narration
 * frame replaces the previous phase rather than accumulating.
 */
export function extractCurrentPhase(intermediate: AssistantBlock[]): CurrentPhase {
  const tools: ToolCallView[] = [];
  for (let i = intermediate.length - 1; i >= 0; i--) {
    const block = intermediate[i];
    if (block.kind === 'tool') {
      tools.unshift(block.tool);
    } else {
      return { narration: block, tools };
    }
  }
  return { narration: null, tools };
}

/**
 * "Smart" auto-scroll gate. Pin to the bottom on a content update only when
 * the user was *already* near the bottom *before* the update — i.e. they were
 * following the stream and the new chunk just landed below their viewport.
 *
 * The gate must look at ``previousScrollHeight`` (the value from before this
 * mutation), not ``scrollHeight``: a single SSE frame can grow the transcript
 * by hundreds of pixels, and if we measured the post-mutation distance we'd
 * find a big gap and (wrongly) decide the user had scrolled up. With the
 * pre-mutation height we ask the right question: "was the user at the bottom
 * before the new content arrived?"
 *
 * ``previousScrollHeight === 0`` is the first-mount case — nothing rendered
 * yet, so we pin to the bottom so the first frames land cleanly.
 */
export function shouldAutoScrollToBottom(
  metrics: {
    scrollHeight: number;
    scrollTop: number;
    clientHeight: number;
    previousScrollHeight: number;
  },
  thresholdPx: number = 100,
): boolean {
  if (metrics.previousScrollHeight === 0) return true;
  const distance =
    metrics.previousScrollHeight - metrics.scrollTop - metrics.clientHeight;
  return distance <= thresholdPx;
}

/**
 * Append a streamed text chunk into the live block list. Consecutive text
 * chunks merge into the trailing text block so a sentence streamed in three
 * frames doesn't become three "paragraphs".
 */
export function appendLiveText(blocks: AssistantBlock[], text: string): AssistantBlock[] {
  const next = blocks.slice();
  pushText(next, text);
  return next;
}

/** Same coalescing rule as text, but for extended-thinking chunks. */
export function appendLiveThinking(
  blocks: AssistantBlock[],
  text: string,
): AssistantBlock[] {
  const next = blocks.slice();
  pushThinking(next, text);
  return next;
}

function pushText(blocks: AssistantBlock[], text: string): void {
  const last = blocks[blocks.length - 1];
  if (last && last.kind === 'text') {
    blocks[blocks.length - 1] = { kind: 'text', text: last.text + text };
  } else {
    blocks.push({ kind: 'text', text });
  }
}

function pushThinking(blocks: AssistantBlock[], text: string): void {
  const last = blocks[blocks.length - 1];
  if (last && last.kind === 'thinking') {
    blocks[blocks.length - 1] = { kind: 'thinking', text: last.text + text };
  } else {
    blocks.push({ kind: 'thinking', text });
  }
}

/**
 * Update a streamed tool's status/result in place. Live ``tool_result`` frames
 * arrive after the matching ``tool_use``, so we find the existing tool block
 * by id and merge the result fields. If no match (an orphan result), the array
 * is returned unchanged — the orchestrator's audit is still the ground truth.
 */
export function applyLiveToolResult(
  blocks: AssistantBlock[],
  toolUseId: string | null,
  isError: boolean,
  summary: string,
): AssistantBlock[] {
  if (!toolUseId) return blocks;
  let changed = false;
  const next = blocks.map((block) => {
    if (
      !changed &&
      block.kind === 'tool' &&
      block.tool.toolUseId === toolUseId
    ) {
      changed = true;
      return {
        kind: 'tool' as const,
        tool: {
          ...block.tool,
          status: isError ? ('error' as const) : ('ok' as const),
          isError,
          resultSummary: summary,
        },
      };
    }
    return block;
  });
  return changed ? next : blocks;
}

// ---------------------------------------------------------------------------
// Transcript timeline
//
// Each turn is one item: user prompts render as a bubble, assistant turns
// render as a sequence of inlined blocks (text / thinking / tool) in the order
// the model emitted them. Tool calls live INSIDE the assistant turn now — they
// no longer cluster above the bubble because the assistant message was
// timestamped at end-of-turn.
// ---------------------------------------------------------------------------

export type TimelineItem =
  | { kind: 'user'; message: MessageRead }
  | {
      kind: 'assistant';
      message: MessageRead;
      blocks: AssistantBlock[];
      /** Files the agent attached this turn (attach_file) — one card each. */
      agentFiles?: AgentFileData[];
      /** Messages a headless conversation agent sent this turn
       *  (send_conversation_message) — what the person on the email/SMS
       *  thread actually received, one card each. */
      sentMessages?: SentMessageData[];
      /** Decisions recorded this turn (record_expired_quote_decision). */
      decisions?: ConversationDecisionData[];
    };

export function buildTranscriptTimeline(
  messages: MessageRead[],
  toolCalls: ToolCallRead[],
): TimelineItem[] {
  const toolsById = new Map<string, ToolCallView>();
  for (const tc of toolCalls) {
    if (tc.tool_use_id) {
      toolsById.set(tc.tool_use_id, toolCallViewFromRead(tc));
    }
  }
  const items: TimelineItem[] = [];
  for (const message of messages) {
    if (message.role === 'assistant') {
      // Successful attach_file frames become file cards (the card replaces
      // the chip); pending/errored attach frames keep their chips.
      const { blocks: afterFiles, files } = liftAgentFileCards(
        expandAssistantBlocks(message, toolsById),
      );
      // Likewise the conversation-channel capture frames (the message a
      // headless agent sent over email/SMS, the decision it recorded) become
      // cards so the transcript reads as the exchange it was.
      const { blocks, sent, decisions } = liftConversationCards(afterFiles);
      items.push({
        kind: 'assistant',
        message,
        blocks,
        ...(files.length ? { agentFiles: files } : {}),
        ...(sent.length ? { sentMessages: sent } : {}),
        ...(decisions.length ? { decisions } : {}),
      });
    } else {
      // System rows aren't user-visible today but we render them as a user-side
      // bubble if any are ever returned, rather than dropping silently.
      items.push({ kind: 'user', message });
    }
  }
  return items;
}

// ---------------------------------------------------------------------------
// REST helpers (axios — baseURL is '/api', so paths omit the prefix)
// ---------------------------------------------------------------------------

/**
 * Request timeout for the per-turn REST setup calls every console door makes
 * (open a conversation, load its detail) before the SSE turn starts. The shared
 * axios default is 30s, which is too tight for a chat surface: a busy API
 * (deploy recreating containers, a saturated worker pool) made a trivial
 * create-conversation POST exceed it and the user saw a raw
 * "timeout of 30000ms exceeded" instead of their answer. Two minutes matches
 * the patience a conversational surface already shows its SSE turn.
 */
export const CONSOLE_DOOR_REQUEST_TIMEOUT_MS = 120_000;

export async function createConversation(
  body: ConversationCreate = {},
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(
    '/admin/agent-console/conversations',
    body,
    { timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS },
  );
  return data;
}

export async function listConversations(
  opts: {
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
    scope?: 'mine' | 'all';
    createdBy?: string;
    q?: string;
  } = {},
): Promise<ConversationRead[]> {
  const params: Record<string, string | number | boolean> = {};
  if (opts.includeArchived) params.include_archived = true;
  if (opts.limit != null) params.limit = opts.limit;
  if (opts.offset != null) params.offset = opts.offset;
  if (opts.scope) params.scope = opts.scope;
  if (opts.createdBy) params.created_by = opts.createdBy;
  if (opts.q) params.q = opts.q;
  const { data } = await api.get<ConversationList>(
    '/admin/agent-console/conversations',
    { params },
  );
  return data.items;
}

export async function listConversationOwners(): Promise<ConversationOwner[]> {
  const { data } = await api.get<{ owners: ConversationOwner[] }>(
    '/admin/agent-console/conversations/owners',
  );
  return data.owners;
}

export async function getConversationDetail(
  conversationId: string,
): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(
    `/admin/agent-console/conversations/${conversationId}`,
    { timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS },
  );
  return data;
}

/**
 * Rename a conversation on ANY console door (PATCH its title) — the shared
 * editable-title path for every Agent SDK door. `basePath` is the door's API
 * prefix (e.g. `/admin/agent-console`, `/quality-copilot`, `/tech-support-copilot`);
 * each door's service exposes a thin `renameConversation(id, title)` wrapper.
 */
export async function renameConsoleConversation(
  basePath: string,
  conversationId: string,
  title: string,
): Promise<ConversationRead> {
  const { data } = await api.patch<ConversationRead>(
    `${basePath}/conversations/${conversationId}`,
    { title },
  );
  return data;
}

/** Rename an Agent Console (analyst) conversation. */
export function renameConversation(
  conversationId: string,
  title: string,
): Promise<ConversationRead> {
  return renameConsoleConversation('/admin/agent-console', conversationId, title);
}

export interface CancelTurnResponse {
  // False (not an error) when there was no live turn to stop — it already
  // finished, or runId pointed at a turn that is no longer running.
  cancelled: boolean;
  run_id: string | null;
}

/**
 * Stop an in-flight turn on any console surface (`basePath` names the door,
 * e.g. `/quality-copilot`, or the analyst console's `/admin/agent-console`).
 * Pass the `runId` from the turn's `started` frame to target it precisely (a
 * stale Stop can't abort a newer turn); omit it to stop whatever turn is
 * currently running. Idempotent — a no-op `{cancelled: false}` is a normal
 * outcome, not an error.
 */
export async function cancelConsoleTurn(
  basePath: string,
  conversationId: string,
  runId?: string | null,
): Promise<CancelTurnResponse> {
  const { data } = await api.post<CancelTurnResponse>(
    `${basePath}/conversations/${conversationId}/cancel`,
    runId ? { run_id: runId } : {},
  );
  return data;
}

/** Stop an Agent Console (analyst) conversation's in-flight turn. */
export function cancelConsoleMessage(
  conversationId: string,
  runId?: string | null,
): Promise<CancelTurnResponse> {
  return cancelConsoleTurn('/admin/agent-console', conversationId, runId);
}

export interface InjectTurnResponse {
  // False (not an error) when there was no live turn to steer — it already
  // finished, or runId pointed at a turn that is no longer running.
  injected: boolean;
  run_id: string | null;
}

/**
 * Steer the conversation's in-flight turn with a mid-run message. The running
 * agent is interrupted and re-queried with `text` on the SAME live SDK session,
 * so it keeps its in-turn context (its tool calls/results) — the injected
 * message + the steered continuation arrive on the existing SSE stream. Pass the
 * `runId` from the turn's `started` frame to target it precisely. Idempotent —
 * `{injected: false}` (no live turn) is a normal outcome, not an error.
 */
export async function injectConsoleMessage(
  conversationId: string,
  text: string,
  runId?: string | null,
): Promise<InjectTurnResponse> {
  const { data } = await api.post<InjectTurnResponse>(
    `/admin/agent-console/conversations/${conversationId}/inject`,
    runId ? { text, run_id: runId } : { text },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Profiles + master access catalog (mirror app/schemas/agent_console.py)
// ---------------------------------------------------------------------------

export type AccessCategory = 'data' | 'knowledge' | 'write';

export interface AccessCapability {
  id: string;
  label: string;
  category: AccessCategory;
  description: string;
  writes: boolean;
}

/** One business data area in plain language (mirror DataDomainRead). */
export interface DataDomain {
  id: string;
  label: string;
  description: string;
  tables: string[];
  scope: string; // 'full' | 'partial' | 'none'
}

/** One source the console can reach + the business domains inside it
 * (mirror AccessGroupRead). */
export interface AccessGroup {
  id: string;
  label: string;
  kind: string; // 'data' | 'knowledge' | 'write'
  description: string;
  scope_note: string;
  domains: DataDomain[];
}

export interface ConsoleProfile {
  id: string;
  name: string;
  description: string;
  writes: boolean;
  // The agent's configured default model — the picker preselects it for a fresh
  // conversation under this profile (admin-tunable per agent).
  default_model: string;
  access: string[];
  // Business-facing view of the data this profile can reach (grouped, plain
  // language). Derived server-side from `access`.
  access_groups: AccessGroup[];
}

export interface AgentConsoleProfilesResponse {
  default_profile_id: string;
  profiles: ConsoleProfile[];
  access_catalog: AccessCapability[];
}

export async function getProfiles(): Promise<AgentConsoleProfilesResponse> {
  const { data } = await api.get<AgentConsoleProfilesResponse>(
    '/admin/agent-console/profiles',
  );
  return data;
}

// ---------------------------------------------------------------------------
// Injected-context manifest — the "inspect configuration" panel
// (mirror app/schemas/agent_console.py: ContextManifestResponse)
// ---------------------------------------------------------------------------

/** One inspectable piece of injected content (prompt, skill, or a not-present
 * accounting of CLAUDE.md / init scripts). */
export interface InjectedDoc {
  kind: string; // 'system_prompt' | 'skill' | 'claude_md' | 'settings' | 'init_script'
  title: string;
  source_path: string | null;
  present: boolean;
  content: string;
  note: string | null;
}

export type ToolSource = 'in_process' | 'kb_mount' | 'sdk_builtin';

/** One tool on the profile's surface, with its description + JSON-Schema input. */
export interface ToolSchema {
  server: string;
  name: string;
  qualified_name: string;
  source: ToolSource;
  description: string;
  input_schema: Record<string, unknown> | null;
  schema_available: boolean;
  writes: boolean;
}

/** The static Claude Agent SDK options the profile runs under. */
export interface SdkConfig {
  model: string;
  setting_sources: string[];
  permission_mode: string;
  cwd_strategy: string;
  max_thinking_tokens: number;
  mcp_servers: string[];
  standing_context_strategy: string;
}

export interface ContextManifest {
  profile_id: string;
  profile_name: string;
  profile_description: string;
  prompt_kind: string;
  writes: boolean;
  sdk: SdkConfig;
  injected_docs: InjectedDoc[];
  tools: ToolSchema[];
}

export async function getProfileManifest(profileId: string): Promise<ContextManifest> {
  const { data } = await api.get<ContextManifest>(
    `/admin/agent-console/profiles/${encodeURIComponent(profileId)}/manifest`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Runtime config (lane timeouts / per-turn cap / concurrency — admin editable)
// ---------------------------------------------------------------------------

export interface AgentConsoleConfig {
  admit_timeout_interactive_seconds: number;
  admit_timeout_background_seconds: number;
  turn_max_seconds: number;
  max_concurrency: number;
  reserved_interactive_slots: number;
}

export type AgentConsoleConfigUpdate = Partial<AgentConsoleConfig>;

export async function getConfig(): Promise<AgentConsoleConfig> {
  const { data } = await api.get<AgentConsoleConfig>('/admin/agent-console/config');
  return data;
}

export async function updateConfig(
  patch: AgentConsoleConfigUpdate,
): Promise<AgentConsoleConfig> {
  const { data } = await api.patch<AgentConsoleConfig>(
    '/admin/agent-console/config',
    patch,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Model selection — the per-agent model picker + admin per-agent defaults
// (mirror app/schemas/agent_console.py: ModelsResponse / ModelConfigRead)
// ---------------------------------------------------------------------------

/** One selectable Anthropic model offered in the picker (mirror ModelOptionRead). */
export interface ModelOption {
  id: string;
  label: string;
  tier: string;
  blurb: string;
  relative_cost: string;
}

/** The picker catalog + the default this surface/agent starts a fresh thread on. */
export interface ModelsResponse {
  models: ModelOption[];
  default_model: string;
  // The most-capable selectable model id (the head of the picker order) — used to
  // render the "try a stronger model" nudge on a 👎 without hardcoding the flagship.
  strongest_model: string;
}

/** The admin-editable per-agent default-model config + the catalog (mirror ModelConfigRead). */
export interface ModelConfig {
  default_model: string;
  profile_overrides: Record<string, string>;
  models: ModelOption[];
}

export type ModelConfigUpdate = {
  default_model?: string;
  profile_overrides?: Record<string, string>;
};

export async function getConsoleModels(): Promise<ModelsResponse> {
  const { data } = await api.get<ModelsResponse>('/admin/agent-console/models');
  return data;
}

export async function getModelConfig(): Promise<ModelConfig> {
  const { data } = await api.get<ModelConfig>('/admin/agent-console/model-config');
  return data;
}

export async function updateModelConfig(patch: ModelConfigUpdate): Promise<ModelConfig> {
  const { data } = await api.patch<ModelConfig>(
    '/admin/agent-console/model-config',
    patch,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Feedback — 👍/👎 + optional comment, one reusable path for every SDK door
// (mirror app/schemas/agent_console.py: FeedbackCreate / FeedbackRead)
// ---------------------------------------------------------------------------

export type FeedbackRating = 'up' | 'down';

export interface FeedbackCreate {
  rating?: FeedbackRating | null;
  comment?: string | null;
  /** The assistant message rated; omit for conversation-level feedback. */
  message_id?: string | null;
}

export interface FeedbackRead {
  id: string;
  conversation_id: string;
  message_id: string | null;
  user_id: string;
  rating: FeedbackRating | null;
  comment: string | null;
  profile_id: string | null;
  model: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Everything the reusable {@link FeedbackControls} component needs to render +
 * submit a reaction for ONE assistant turn. Each door page builds this from the
 * turn's persisted message id + the model that answered, plus the page-level
 * conversation id, door API base path, and the strongest selectable model (for
 * the 👎 "try a stronger model" nudge).
 */
export interface FeedbackContext {
  /** Door API base path, e.g. '/quality-copilot' or '/admin/agent-console'. */
  basePath: string;
  conversationId: string;
  /** The real persisted assistant message id (from the `done` frame or detail). */
  messageId: string;
  /** The model that produced this turn (for the stronger-model nudge). */
  modelUsed?: string | null;
  /** The most-capable selectable model id + label (from GET /models). */
  strongestModelId?: string | null;
  strongestModelLabel?: string | null;
}

/**
 * Upsert the caller's reaction to one answer (or the whole thread) on ANY console
 * door — the shared write path behind {@link FeedbackContext}. `basePath` is the
 * door's API prefix; last-write-wins server-side (one row per user+target).
 */
export async function submitFeedback(
  basePath: string,
  conversationId: string,
  body: FeedbackCreate,
): Promise<FeedbackRead> {
  const { data } = await api.post<FeedbackRead>(
    `${basePath}/conversations/${conversationId}/feedback`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Streaming turn (raw fetch — full '/api/...' path + explicit Bearer)
// ---------------------------------------------------------------------------

/**
 * Shown when the SSE stream drops mid-turn with no terminal frame — the server
 * was almost certainly restarted by a deploy. Calm + actionable: the turn is
 * recovered server-side (the conversation is un-stuck), so the user can resend.
 */
export const STREAM_INTERRUPTED_MESSAGE =
  'The connection was interrupted — the server may have restarted. ' +
  'This answer may be incomplete; please resend your message.';

/**
 * The message for a failure BEFORE the stream opened — the REST setup calls
 * (create the conversation, upload an attachment) that run ahead of each turn.
 * An axios timeout reads as "the server took too long", a server refusal reads
 * as its own `detail`, and anything else falls back to `fallback` — never the
 * raw axios text ("timeout of 30000ms exceeded") the user once saw verbatim.
 */
export function turnSetupErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    code?: string;
    message?: string;
    response?: { status?: number; data?: { detail?: unknown } };
  } | null;
  if (!e || typeof e !== 'object') return fallback;
  const msg = typeof e.message === 'string' ? e.message : '';
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || /^timeout of \d+ms exceeded/i.test(msg)) {
    const secs = Math.round(CONSOLE_DOOR_REQUEST_TIMEOUT_MS / 1000);
    return (
      `The server took more than ${secs}s to respond — it may be busy or ` +
      'restarting. Your message was not sent; please try again in a moment.'
    );
  }
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (e.response?.status != null) return `${fallback} (HTTP ${e.response.status})`;
  return msg || fallback;
}

/** The synthetic terminal `error` frame emitted on a mid-turn stream drop. */
export function streamInterruptedEvent(): ErrorEvent {
  return { type: 'error', message: STREAM_INTERRUPTED_MESSAGE };
}

/**
 * The message for a stream that never opened: the server's own `detail` when it
 * sent one, else the bare status.
 *
 * Every refusal on this endpoint is a sentence written for the person reading it
 * — "a turn is already running", "this conversation ran under an agent that no
 * longer exists, its history stays readable". Reporting `HTTP 409` instead threw
 * all of that away and told the user only that something was wrong.
 */
export async function streamErrorMessage(resp: Response): Promise<string> {
  try {
    const body = (await resp.clone().json()) as { detail?: unknown };
    if (typeof body?.detail === 'string' && body.detail.trim()) {
      return body.detail;
    }
  } catch {
    // Not JSON (a proxy's HTML 502, an empty body) — fall through to the status.
  }
  return `console stream HTTP ${resp.status}`;
}

/**
 * Open an SSE stream for ONE console-door turn and invoke `onEvent` for every
 * parsed frame; resolves when the server closes the stream (after `done`). Throws
 * only on a non-OK HTTP response — turn-level failures arrive as `error` frames,
 * exactly as the orchestrator guarantees.
 *
 * The single SSE stream loop shared by every Agent SDK door (Agent Console,
 * Quality Co-Pilot, Tech Support Co-Pilot, …): each door's `stream*Message` is a
 * one-line wrapper that supplies its endpoint + payload. The raw fetch bypasses
 * the axios response interceptor that auto-refreshes on a 401, so this mirrors it
 * — on a 401 it refreshes the access token once and retries the stream (a token
 * can lapse while a tab idles through a deploy, despite a valid refresh token
 * still in storage; see kanban 941f0fd0).
 *
 * @param path    Full `/api/...` message endpoint for the door.
 * @param payload JSON body — `{ prompt, model?, profile_id? }` (the locked doors
 *                omit `profile_id`; the profile is forced server-side).
 */
export async function streamConsoleSse(
  path: string,
  payload: Record<string, unknown>,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const body = JSON.stringify(payload);
  const sendStream = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(path, { method: 'POST', headers, credentials: 'include', body, signal });
  };

  let resp = await sendStream(tokenService.getAccessToken());
  if (resp.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) resp = await sendStream(refreshed);
  }
  if (!resp.ok || !resp.body) {
    throw new Error(await streamErrorMessage(resp));
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  // The orchestrator ends EVERY turn with a terminal frame (`done`, and on a
  // failure/stop an `error`/`cancelled` right before it). If the byte stream ends
  // WITHOUT one, the turn didn't finish — the server was cut off mid-turn (a deploy
  // restart drops the SSE socket). We must not let that read as success: track
  // whether a terminal frame arrived and, if not, surface a calm interruption.
  let sawTerminal = false;
  const emit = (evt: ConsoleEvent): void => {
    if (evt.type === 'done' || evt.type === 'error' || evt.type === 'cancelled') {
      sawTerminal = true;
    }
    onEvent(evt);
  };
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const { records, rest } = splitSseRecords(buf);
      buf = rest;
      for (const record of records) {
        const evt = parseConsoleEvent(record);
        if (evt) emit(evt);
      }
    }
    // Flush a final record that arrived without its trailing blank line.
    const tail = buf.trim();
    if (tail) {
      const evt = parseConsoleEvent(tail);
      if (evt) emit(evt);
    }
  } catch {
    // The read threw mid-stream — an abrupt socket reset (the container was
    // recreated by a deploy). The error itself carries nothing actionable (the
    // interruption event is the signal), so it is deliberately not bound. If a
    // terminal frame already arrived this is just a harmless trailing read
    // error; otherwise report the interruption. Resolve (don't reject) so the
    // caller treats it like any other terminal frame.
    if (!sawTerminal) onEvent(streamInterruptedEvent());
    return;
  }
  // The reader ended cleanly (a graceful socket close on shutdown) but no terminal
  // frame arrived — the same interruption, just without a thrown error.
  if (!sawTerminal) onEvent(streamInterruptedEvent());
}

/**
 * Agent Console (analyst) turn — wraps {@link streamConsoleSse} with the
 * console's endpoint + payload. It carries the selected profile + model so a
 * mid-thread switch sticks (last-turn-wins, persisted server-side); omit either
 * to keep the conversation's current value.
 */
export async function streamConsoleMessage(
  conversationId: string,
  prompt: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
  profileId?: string,
  model?: string,
): Promise<void> {
  const payload: { prompt: string; profile_id?: string; model?: string } = { prompt };
  if (profileId) payload.profile_id = profileId;
  if (model) payload.model = model;
  return streamConsoleSse(
    `/api/admin/agent-console/conversations/${conversationId}/message`,
    payload,
    onEvent,
    signal,
  );
}

// ---------------------------------------------------------------------------
// Reattach (GET) — reconnect to an in-flight turn after a refresh / switch-back
//
// The turn now runs DETACHED from the POST that started it (server-side
// broadcast), so a reloaded page can pick the live stream back up: this GET
// replays the frames streamed so far, then tails live to the terminal frame. It
// resolves immediately on a lone `done` frame when no turn is live. Kept separate
// from streamConsoleSse's POST path (own GET request, no body) but shares the pure
// SSE parse helpers + the same "no terminal frame => calm interruption" contract.
// ---------------------------------------------------------------------------

/**
 * Open the reattach GET stream for a console-door conversation and invoke
 * `onEvent` for every parsed frame; resolves when the server closes the stream.
 * Mirrors {@link streamConsoleSse}'s Bearer + 401-refresh + interruption handling.
 *
 * @param path Full `/api/...` reattach endpoint (`.../conversations/{id}/stream`).
 */
export async function streamConsoleReattach(
  path: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const sendStream = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(path, { method: 'GET', headers, credentials: 'include', signal });
  };

  let resp = await sendStream(tokenService.getAccessToken());
  if (resp.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) resp = await sendStream(refreshed);
  }
  if (!resp.ok || !resp.body) {
    throw new Error(`console reattach HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let sawTerminal = false;
  const emit = (evt: ConsoleEvent): void => {
    if (evt.type === 'done' || evt.type === 'error' || evt.type === 'cancelled') {
      sawTerminal = true;
    }
    onEvent(evt);
  };
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const { records, rest } = splitSseRecords(buf);
      buf = rest;
      for (const record of records) {
        const evt = parseConsoleEvent(record);
        if (evt) emit(evt);
      }
    }
    const tail = buf.trim();
    if (tail) {
      const evt = parseConsoleEvent(tail);
      if (evt) emit(evt);
    }
  } catch {
    if (!sawTerminal) onEvent(streamInterruptedEvent());
    return;
  }
  if (!sawTerminal) onEvent(streamInterruptedEvent());
}

/**
 * Agent Console (analyst) reattach — reconnect to `conversationId`'s in-flight
 * turn. Wraps {@link streamConsoleReattach} with the console's stream endpoint.
 */
export function reattachConsoleMessage(
  conversationId: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return streamConsoleReattach(
    `/api/admin/agent-console/conversations/${conversationId}/stream`,
    onEvent,
    signal,
  );
}
